// Pure POSIX-ish path resolution for the Terminal shell.
//
// Every filesystem command in the shell routes through resolvePath, so that
// `cd ../..` and `ls ../..` can never disagree. No imports: this runs in the
// browser bundle and under `node --test` unchanged.
//
// KEEP IN SYNC with ./shell-path.d.mts (hand-written type mirror).

/**
 * Collapse a list of raw segments into normalised absolute-path segments.
 * `.` and empty segments vanish; `..` pops, and popping past the root is a
 * no-op so the result can never contain "..".
 * @param {string[]} segments
 * @returns {string[]}
 */
function normalise(segments) {
  /** @type {string[]} */
  const out = [];
  for (const seg of segments) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") {
      // Clamp at the root rather than emitting "/..".
      if (out.length > 0) out.pop();
      continue;
    }
    out.push(seg);
  }
  return out;
}

/**
 * Resolve `arg` against `cwd`, expanding a leading `~` to `home`.
 * Always returns an absolute, normalised path with no trailing slash
 * (except the root, which is "/").
 *
 * @param {string} cwd current working directory
 * @param {string} [arg] the user's argument; empty/absent means "cwd"
 * @param {string} [home] the user's home path, for `~` expansion
 * @returns {string}
 */
export function resolvePath(cwd, arg, home) {
  const base = typeof cwd === "string" ? cwd.trim() : "";
  const raw = typeof arg === "string" ? arg.trim() : "";
  const homeDir = typeof home === "string" ? home.trim() : "";

  let target = raw;
  if (target === "~" || target.startsWith("~/")) {
    // Only a bare ~ or ~/… expands; "~files" is an ordinary relative name.
    const rest = target.slice(1);
    target = (homeDir || "/") + rest;
  }

  const segments = target.startsWith("/")
    ? target.split("/")
    : [...base.split("/"), ...target.split("/")];

  return "/" + normalise(segments).join("/");
}

/**
 * Shorten `path` for display: paths inside `home` render with a leading `~`.
 * A sibling that merely shares a string prefix (`/home/user2` vs `/home/u`)
 * is left alone.
 *
 * @param {string} path
 * @param {string} [home]
 * @returns {string}
 */
export function displayPath(path, home) {
  const p = typeof path === "string" && path ? path : "/";
  const h = typeof home === "string" ? home.trim() : "";
  if (!h || h === "/") return p;
  if (p === h) return "~";
  if (p.startsWith(h + "/")) return "~" + p.slice(h.length);
  return p;
}
